/**
 * 推薦點譯文的採用規則：把勾選的建議攤成「每筆推薦點一個 PUT」。
 *
 * ## 為什麼推薦點是直接寫入，景點欄位卻要按儲存
 *
 * 不是雙重標準，是跟著各自原本的儲存語意走。景點的文字欄位隨表單一起 PATCH，所以
 * 譯文填進表單、等使用者按「儲存變更」；推薦點本來就是**即時儲存**（卡片失焦就送
 * 出），沒有表單可以暫存——把它硬塞進景點表單只會多出一種「看得到但存不了」的狀態。
 *
 * 採用的當下就寫進去，但每一格都同時帶上 `mt_draft` 記號：機翻仍然是待校的，只是
 * 待校的地方在資料庫而不是表單。
 *
 * ## 為什麼一筆推薦點只送一個請求
 *
 * 一家店可能同時採用店名、簡介、營業時間 × 兩個語系＝ 6 格。逐格送就是 6 個 PUT
 * 打同一列，而 i18n 物件是整包取代——後到的那個會拿著自己讀到的舊版本覆蓋回去，
 * 結果是只有最後一格生效。合併成一個 patch 才不會互相蓋掉。
 */

import type { SpotPoi, SpotPoiPatch, SpotTranslateItem } from '../lib/api-client';

/** 推薦點欄位的鍵前綴；與後端 `POI_FIELD_PREFIX` 一致。 */
export const POI_FIELD_PREFIX = 'poi.';

/** 建議是不是推薦點的欄位。看 `poi` 而不是看前綴——前者是後端真的填了才有。 */
export function isPoiItem(item: SpotTranslateItem): boolean {
  return item.poi !== undefined && item.poi !== null;
}

/** `poi.name_i18n` → `name_i18n`（資料表欄位名）。 */
export function poiColumnOf(field: string): string {
  return field.startsWith(POI_FIELD_PREFIX) ? field.slice(POI_FIELD_PREFIX.length) : field;
}

export interface PoiApplyTarget {
  poiId: string;
  category: SpotPoi['category'];
  seq: number;
  /** 這一筆要送出去的完整 patch（含機翻記號）。 */
  patch: SpotPoiPatch;
  /** 這次寫了幾格，給畫面上的計數用。 */
  cells: number;
}

export interface PoiApplyPlan {
  targets: PoiApplyTarget[];
  /**
   * 找不到對應推薦點的建議數。
   *
   * 產生建議之後、按下採用之前，有人把那筆推薦點刪掉或重排了。這種格子不能猜著
   * 寫回去（序號是位置，會落到別家店身上），只能跳過並讓畫面講出來。
   */
  orphaned: number;
}

/**
 * 把勾選的推薦點建議整理成要送出的 PUT。
 *
 * @param items 使用者勾選、且屬於推薦點的建議
 * @param pois  目前伺服器上的推薦點；用來把譯文併進既有的 i18n 物件
 */
export function buildPoiApplyPlan(
  items: readonly SpotTranslateItem[],
  pois: readonly SpotPoi[],
): PoiApplyPlan {
  const byId = new Map(pois.map((p) => [p.id, p]));
  const targets = new Map<string, PoiApplyTarget>();
  let orphaned = 0;

  for (const item of items) {
    const ref = item.poi;
    if (!ref) continue;
    const poi = byId.get(ref.id);
    if (!poi) {
      orphaned += 1;
      continue;
    }

    const column = poiColumnOf(item.field);
    const target = targets.get(ref.id) ?? {
      poiId: ref.id,
      category: poi.category,
      seq: poi.seq,
      patch: {},
      cells: 0,
    };

    // 併進**目前伺服器上的**那一份，而不是只送這一格：i18n 欄位是整包取代，
    // 只送 { en: … } 會把中文主稿一起清掉
    const patch = target.patch as Record<string, Record<string, string>>;
    const base = patch[column] ?? (poi[column as keyof SpotPoi] as Record<string, string>) ?? {};
    patch[column] = { ...base, [item.locale]: item.translated_text };

    const state = (target.patch.translation_state ?? {}) as Record<
      string,
      Record<string, 'mt_draft' | null>
    >;
    state[column] = { ...(state[column] ?? {}), [item.locale]: 'mt_draft' };
    target.patch.translation_state = state;

    target.cells += 1;
    targets.set(ref.id, target);
  }

  return { targets: [...targets.values()], orphaned };
}

/**
 * 人手動改了某一格 → 解除該格的機翻記號。
 *
 * 沒有記號時回 undefined（不送這個欄位）：每次失焦都附一份 `{...: null}` 會讓
 * 稽核紀錄裡塞滿沒有意義的「解除」，而真的有人校對過的那一次就淹沒在裡面。
 */
export function clearPoiMarkPatch(
  poi: SpotPoi,
  column: string,
  locale: string,
): SpotPoiPatch['translation_state'] | undefined {
  const state = poi.translation_state as Record<string, Record<string, string>> | undefined;
  if (!state?.[column]?.[locale]) return undefined;
  return { [column]: { [locale]: null } };
}

/** 這個景點的推薦點總共還有幾格機翻待校。 */
export function countPoiMtDrafts(pois: readonly SpotPoi[]): number {
  return pois.reduce(
    (n, poi) =>
      n +
      Object.values((poi.translation_state ?? {}) as Record<string, Record<string, string>>).reduce(
        (m, locales) => m + Object.keys(locales).length,
        0,
      ),
    0,
  );
}

/** 某個分類、某個語系底下有沒有機翻待校的格子（總覽的黃格子）。 */
export function poiCategoryHasMt(pois: readonly SpotPoi[], locale: string): boolean {
  return pois.some((poi) =>
    Object.values((poi.translation_state ?? {}) as Record<string, Record<string, string>>).some(
      (locales) => locales[locale] !== undefined,
    ),
  );
}

/** 把一筆推薦點的機翻記號全部清掉；沒有記號時回 undefined。 */
export function clearAllPoiMarks(poi: SpotPoi): SpotPoiPatch['translation_state'] | undefined {
  const state = (poi.translation_state ?? {}) as Record<string, Record<string, string>>;
  const patch: Record<string, Record<string, null>> = {};
  for (const [column, locales] of Object.entries(state)) {
    for (const locale of Object.keys(locales)) {
      patch[column] = { ...(patch[column] ?? {}), [locale]: null };
    }
  }
  return Object.keys(patch).length === 0 ? undefined : patch;
}
